import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { LogOut, Plus, Trash2, QrCode, ExternalLink, X, Loader2, Activity, ChevronDown } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { compressImage } from '../lib/compressImage'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../hooks/useToast'
import Toggle from '../components/Toggle'
import QRCard from '../components/QRCard'
import Toast from '../components/Toast'
import ParkingSession from '../components/ParkingSession'

const VEHICLE_ICONS = { car: '🚗', bike: '🏍️', truck: '🚚', auto: '🛺', pet: '🐾', child: '👶' }
const PET_ANIMAL_ICONS = { dog: '🐕', cat: '🐈', bird: '🐦', rabbit: '🐇', other: '🐾' }
const METHOD_ICONS = { call: '📞', whatsapp: '💬', sms: '✉️' }

// Per-category color palette
const TAG_COLORS = {
  vehicle: { accent: '#00e5a0', border: 'rgba(0,229,160,0.22)', bg: 'rgba(0,229,160,0.06)', scanBg: 'rgba(0,229,160,0.12)', scanColor: '#00e5a0' },
  child:   { accent: '#60a5fa', border: 'rgba(96,165,250,0.25)', bg: 'rgba(96,165,250,0.06)', scanBg: 'rgba(96,165,250,0.12)', scanColor: '#60a5fa' },
  pet:     { accent: '#fbbf24', border: 'rgba(251,191,36,0.25)',  bg: 'rgba(251,191,36,0.06)',  scanBg: 'rgba(251,191,36,0.12)',  scanColor: '#fbbf24' },
}
function tagColors(type) {
  if (type === 'child') return TAG_COLORS.child
  if (type === 'pet') return TAG_COLORS.pet
  return TAG_COLORS.vehicle
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const { toast, showToast, dismissToast } = useToast()

  const [profile, setProfile] = useState(null)
  const [vehicles, setVehicles] = useState([])
  const [tags, setTags] = useState({}) // vehicleId → tag
  const [events, setEvents] = useState([])
  const [loadingData, setLoadingData] = useState(true)

  const [showQR, setShowQR] = useState(null) // tagCode or null
  const [showActivity, setShowActivity] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  // Edit tag
  const [editVehicle, setEditVehicle] = useState(null)
  const [editPlate, setEditPlate] = useState('')
  const [editPetName, setEditPetName] = useState('')
  const [editPetBreed, setEditPetBreed] = useState('')
  const [editLoading, setEditLoading] = useState(false)

  // Add vehicle form
  const [newType, setNewType] = useState('car')
  const [newPlate, setNewPlate] = useState('')
  const [newEmergency, setNewEmergency] = useState('')
  const [newPetName, setNewPetName] = useState('')
  const [newPetAnimal, setNewPetAnimal] = useState('dog')
  const [newPetBreed, setNewPetBreed] = useState('')
  const [newPhotoFile, setNewPhotoFile] = useState(null)
  const [newPhotoPreview, setNewPhotoPreview] = useState(null)
  const [addLoading, setAddLoading] = useState(false)

  // Redirect unauthenticated users
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/onboarding')
    }
  }, [user, authLoading, navigate])

  const loadData = useCallback(async () => {
    if (!user) return
    setLoadingData(true)

    try {
      // Profile
      const { data: prof } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single()
      setProfile(prof)

      // Vehicles
      const { data: vehs, error: vehErr } = await supabase
        .from('vehicles')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (vehErr) throw vehErr
      setVehicles(vehs || [])

      // Tags for each vehicle
      if (vehs?.length) {
        const { data: tagData } = await supabase
          .from('tags')
          .select('*')
          .in('vehicle_id', vehs.map((v) => v.id))

        const tagMap = {}
        tagData?.forEach((t) => { tagMap[t.vehicle_id] = t })
        setTags(tagMap)

        // Recent events (last 10 across all tags)
        if (tagData?.length) {
          const { data: evts } = await supabase
            .from('contact_events')
            .select('*, tags(vehicle_id, vehicles(plate, type))')
            .in('tag_id', tagData.map((t) => t.id))
            .order('created_at', { ascending: false })
            .limit(10)
          setEvents(evts || [])
        }
      }
    } catch (err) {
      showToast(err.message || 'Failed to load data', 'error')
    } finally {
      setLoadingData(false)
    }
  }, [user, showToast])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Toggle vehicle active status
  async function toggleActive(vehicle) {
    const { error } = await supabase
      .from('vehicles')
      .update({ is_active: !vehicle.is_active })
      .eq('id', vehicle.id)

    if (error) { showToast('Failed to update', 'error'); return }
    setVehicles((v) => v.map((vv) => vv.id === vehicle.id ? { ...vv, is_active: !vv.is_active } : vv))
  }

  // Toggle channel
  async function toggleChannel(vehicle, channel) {
    const update = { [channel]: !vehicle[channel] }
    const { error } = await supabase.from('vehicles').update(update).eq('id', vehicle.id)
    if (error) { showToast('Failed to update channel', 'error'); return }
    setVehicles((v) => v.map((vv) => vv.id === vehicle.id ? { ...vv, ...update } : vv))
  }

  // Delete vehicle
  function openEdit(v) {
    setEditVehicle(v)
    setEditPlate(v.plate || '')
    setEditPetName(v.pet_name || '')
    setEditPetBreed(v.pet_breed || '')
  }

  async function handleEditSave(e) {
    e.preventDefault()
    setEditLoading(true)
    const isNonVehicle = editVehicle.type === 'pet' || editVehicle.type === 'child'
    const updates = isNonVehicle
      ? { plate: editPetName.trim(), pet_name: editPetName.trim(), pet_breed: editPetBreed.trim() || null }
      : { plate: editPlate.trim().toUpperCase() }

    const { error } = await supabase.from('vehicles').update(updates).eq('id', editVehicle.id)
    setEditLoading(false)

    if (error) { showToast('Failed to update', 'error'); return }
    setVehicles((vs) => vs.map((v) => v.id === editVehicle.id ? { ...v, ...updates } : v))
    setEditVehicle(null)
    showToast('Updated successfully!')
  }

  async function deleteVehicle(vehicleId) {
    setDeletingId(vehicleId)
    const { error } = await supabase.from('vehicles').delete().eq('id', vehicleId)
    setDeletingId(null)

    if (error) { showToast('Failed to delete vehicle', 'error'); return }
    setVehicles((v) => v.filter((vv) => vv.id !== vehicleId))
    showToast('Vehicle removed')
  }

  // Add vehicle
  async function handleAddVehicle(e) {
    e.preventDefault()
    const isNonVehicle = newType === 'pet' || newType === 'child'
    if (newType === 'pet' && !newPetName.trim()) { showToast("Enter your pet's name", 'error'); return }
    if (newType === 'child' && !newPetName.trim()) { showToast("Enter your child's name", 'error'); return }
    if (!isNonVehicle && !newPlate.trim()) { showToast('Enter a plate number', 'error'); return }

    setAddLoading(true)
    try {
      // Upload photo if provided
      let photoUrl = null
      if (newPhotoFile && isNonVehicle) {
        const compressed = await compressImage(newPhotoFile)
        const path = `${user.id}/${Date.now()}.jpg`
        const { error: uploadErr } = await supabase.storage
          .from('tag-photos')
          .upload(path, compressed, { upsert: true })
        if (!uploadErr) {
          const { data: { publicUrl } } = supabase.storage.from('tag-photos').getPublicUrl(path)
          photoUrl = publicUrl
        }
      }

      const insertData = {
        user_id: user.id,
        type: newType,
        emergency_phone: newEmergency.trim() || null,
        ...(isNonVehicle
          ? { plate: newPetName.trim(), pet_name: newPetName.trim(), pet_breed: newPetBreed.trim() || null, ...(newType === 'pet' && { pet_animal: newPetAnimal }), ...(photoUrl && { photo_url: photoUrl }) }
          : { plate: newPlate.trim().toUpperCase() }
        ),
      }

      const { data: vehicle, error: vehErr } = await supabase
        .from('vehicles')
        .insert(insertData)
        .select()
        .single()

      if (vehErr) throw vehErr

      const tagCode = crypto.randomUUID()
      const { error: tagErr } = await supabase
        .from('tags')
        .insert({ vehicle_id: vehicle.id, tag_code: tagCode })

      if (tagErr) throw tagErr

      setShowAddModal(false)
      setNewPlate('')
      setNewType('car')
      setNewEmergency('')
      setNewPetName('')
      setNewPetAnimal('dog')
      setNewPetBreed('')
      setNewPhotoFile(null)
      setNewPhotoPreview(null)
      showToast(newType === 'pet' ? 'Pet tag added!' : newType === 'child' ? 'Child tag added!' : 'Vehicle added!')
      loadData()
    } catch (err) {
      showToast(err.message || 'Failed to add vehicle', 'error')
    } finally {
      setAddLoading(false)
    }
  }

  async function handleSignOut() {
    await supabase.auth.signOut()
    navigate('/')
  }

  const scanUrl = (tagCode) =>
    `${window.location.origin}/scan/${tagCode}`

  if (authLoading || loadingData) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} className="animate-spin text-accent" />
          <p className="text-sm text-brand-muted">Loading your dashboard…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-bg text-brand-text font-sans pb-20">
      <Toast toast={toast} onDismiss={dismissToast} />

      {/* Header */}
      <header
        className="sticky top-0 z-40 px-5 py-4 flex items-center justify-between"
        style={{ background: 'rgba(10,10,15,0.9)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
      >
        <span className="font-syne font-extrabold text-xl">
          Park<span className="text-accent">Ping</span>
        </span>
        <div className="flex items-center gap-3">
          {profile && (
            <span className="text-sm text-brand-muted hidden sm:block">
              Hi, {profile.name.split(' ')[0]}
            </span>
          )}
          <button
            onClick={handleSignOut}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium text-brand-muted hover:text-brand-text transition-colors"
            style={{ background: '#13131a' }}
          >
            <LogOut size={14} />
            Sign Out
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-[480px] px-5 pt-6 space-y-6">
        {/* My Tags */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-syne font-bold text-xl">My Tags</h2>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-bg transition-all hover:opacity-90"
              style={{ background: '#00e5a0' }}
            >
              <Plus size={14} />
              Add Tag
            </button>
          </div>

          {vehicles.length === 0 ? (
            <div
              className="rounded-2xl p-8 text-center"
              style={{ background: '#13131a', border: '1px dashed rgba(255,255,255,0.1)' }}
            >
              <p className="text-3xl mb-2">🏷️</p>
              <p className="text-brand-muted text-sm">No tags yet. Add your first one!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {vehicles.map((v) => {
                const tag = tags[v.id]
                const scanCount = events.filter((e) => e.tag_id === tag?.id).length
                const colors = tagColors(v.type)
                return (
                  <div
                    key={v.id}
                    className="rounded-2xl overflow-hidden flex"
                    style={{
                      background: '#13131a',
                      border: `1px solid ${v.is_active ? colors.border : 'rgba(255,255,255,0.07)'}`,
                    }}
                  >
                    {/* Bold left colour stripe */}
                    <div style={{ width: '4px', background: v.is_active ? colors.accent : 'rgba(255,255,255,0.1)', flexShrink: 0 }} />

                    <div className="flex-1 p-4 space-y-3">
                    {/* Header */}
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0"
                        style={{ background: colors.bg, border: `1px solid ${colors.border}` }}
                      >
                        {v.type === 'pet' ? (PET_ANIMAL_ICONS[v.pet_animal] || '🐾') : (VEHICLE_ICONS[v.type] || '🚗')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="font-bold tracking-widest text-brand-text">
                            {(v.type === 'pet' || v.type === 'child') ? v.pet_name : v.plate}
                          </p>
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider"
                            style={{ background: colors.bg, color: colors.accent, border: `1px solid ${colors.border}` }}>
                            {v.type === 'child' ? 'Child' : v.type === 'pet' ? 'Pet' : 'Vehicle'}
                          </span>
                        </div>
                        <p className="text-xs text-brand-muted capitalize">
                          {v.type === 'pet'
                            ? `${v.pet_animal || 'Pet'}${v.pet_breed ? ` · ${v.pet_breed}` : ''}`
                            : v.type === 'child'
                            ? `Child${v.pet_breed ? ` · ${v.pet_breed}` : ''}`
                            : v.type}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {scanCount > 0 && (
                          <span
                            className="px-2 py-0.5 rounded-full text-xs font-bold"
                            style={{ background: colors.scanBg, color: colors.scanColor }}
                          >
                            {scanCount} scans
                          </span>
                        )}
                        <Toggle checked={v.is_active} onChange={() => toggleActive(v)} />
                      </div>
                    </div>

                    {/* Channel toggles */}
                    <div
                      className="flex items-center justify-between gap-2 py-2 px-3 rounded-xl"
                      style={{ background: '#1c1c26' }}
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs">📞</span>
                        <span className="text-xs text-brand-muted">Call</span>
                        <Toggle
                          checked={v.channel_call}
                          onChange={() => toggleChannel(v, 'channel_call')}
                          disabled={!v.is_active}
                        />
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs">💬</span>
                        <span className="text-xs text-brand-muted">WA</span>
                        <Toggle
                          checked={v.channel_whatsapp}
                          onChange={() => toggleChannel(v, 'channel_whatsapp')}
                          disabled={!v.is_active}
                        />
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs">✉️</span>
                        <span className="text-xs text-brand-muted">SMS</span>
                        <Toggle
                          checked={v.channel_sms}
                          onChange={() => toggleChannel(v, 'channel_sms')}
                          disabled={!v.is_active}
                        />
                      </div>
                    </div>

                    {/* Parking session — vehicles only */}
                    {tag && v.type !== 'pet' && v.type !== 'child' && (
                      <ParkingSession tagCode={tag.tag_code} userId={user.id} plate={v.plate} />
                    )}

                    {/* Action buttons */}
                    <div className="flex gap-2">
                      {tag && (
                        <>
                          <button
                            onClick={() => setShowQR(tag.tag_code)}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-90"
                            style={{ background: 'rgba(0,229,160,0.12)', color: '#00e5a0', border: '1px solid rgba(0,229,160,0.25)' }}
                          >
                            <QrCode size={13} />
                            View Tag
                          </button>
                          <a
                            href={`/scan/${tag.tag_code}`}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-90"
                            style={{ background: '#1c1c26', color: '#7a798a', border: '1px solid rgba(255,255,255,0.07)' }}
                          >
                            <ExternalLink size={13} />
                          </a>
                        </>
                      )}
                      <button
                        onClick={() => openEdit(v)}
                        className="flex items-center justify-center px-3 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-90"
                        style={{ background: '#1c1c26', color: '#7a798a', border: '1px solid rgba(255,255,255,0.07)' }}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => deleteVehicle(v.id)}
                        disabled={deletingId === v.id}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-90 disabled:opacity-50"
                        style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)' }}
                      >
                        {deletingId === v.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                      </button>
                    </div>
                    </div> {/* end flex-1 wrapper */}
                  </div>
                )
              })}
            </div>
          )}
        </section>

        {/* Recent Activity */}
        <section>
          <button
            onClick={() => setShowActivity((v) => !v)}
            className="w-full flex items-center justify-between mb-3 group"
          >
            <div className="flex items-center gap-2">
              <Activity size={16} className="text-accent" />
              <h2 className="font-syne font-bold text-xl">Recent Activity</h2>
              {events.length > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                  style={{ background: 'rgba(0,229,160,0.12)', color: '#00e5a0' }}>
                  {events.length}
                </span>
              )}
            </div>
            <ChevronDown
              size={18}
              className="text-brand-muted transition-transform duration-300"
              style={{ transform: showActivity ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
          </button>

          {showActivity && (
            events.length === 0 ? (
              <div
                className="rounded-2xl p-6 text-center"
                style={{ background: '#13131a', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <p className="text-brand-muted text-sm">No scans yet. Share your QR tag to get started!</p>
              </div>
            ) : (
              <div
                className="rounded-2xl overflow-hidden"
                style={{ background: '#13131a', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                {events.map((evt, i) => (
                  <div
                    key={evt.id}
                    className="flex items-center gap-3 px-4 py-3"
                    style={{ borderTop: i > 0 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}
                  >
                    <span className="text-lg shrink-0">{METHOD_ICONS[evt.method]}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-brand-text capitalize">
                        {evt.method} attempt
                      </p>
                      {evt.tags?.vehicles && (
                        <p className="text-xs text-brand-muted truncate">
                          {VEHICLE_ICONS[evt.tags.vehicles.type]} {evt.tags.vehicles.plate}
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-brand-muted shrink-0">{timeAgo(evt.created_at)}</span>
                  </div>
                ))}
              </div>
            )
          )}
        </section>
      </div>

      {/* QR Modal */}
      {showQR && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-5"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
          onClick={(e) => e.target === e.currentTarget && setShowQR(null)}
        >
          <div className="w-full max-w-sm animate-slide-up">
            <div className="flex justify-end mb-2">
              <button
                onClick={() => setShowQR(null)}
                className="p-2 rounded-full text-brand-muted hover:text-brand-text transition-colors"
                style={{ background: '#13131a' }}
              >
                <X size={18} />
              </button>
            </div>
            {(() => {
              const v = vehicles.find((v) => tags[v.id]?.tag_code === showQR)
              return (
                <QRCard
                  url={scanUrl(showQR)}
                  name={profile?.name}
                  plate={(v?.type === 'pet' || v?.type === 'child') ? v?.pet_name : v?.plate}
                  type={v?.type}
                />
              )
            })()}
          </div>
        </div>
      )}

      {/* Add Vehicle Modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
          onClick={(e) => e.target === e.currentTarget && setShowAddModal(false)}
        >
          <div
            className="w-full max-w-[480px] rounded-t-3xl sm:rounded-3xl p-5 animate-slide-up"
            style={{ background: '#13131a', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-syne font-bold text-lg">{newType === 'pet' ? 'Add Pet Tag' : newType === 'child' ? 'Add Child Tag' : 'Add Vehicle'}</h3>
              <button onClick={() => setShowAddModal(false)} className="text-brand-muted hover:text-brand-text">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleAddVehicle} className="space-y-4">
              {/* Type selector — 6 options in a 3+3 grid */}
              <div className="grid grid-cols-3 gap-2">
                {[{ value: 'car', label: 'Car', icon: '🚗' }, { value: 'bike', label: 'Bike', icon: '🏍️' }, { value: 'truck', label: 'Truck', icon: '🚚' }, { value: 'auto', label: 'Auto', icon: '🛺' }, { value: 'child', label: 'Child', icon: '👶' }, { value: 'pet', label: 'Pet', icon: '🐾' }].map((vt) => (
                  <button
                    key={vt.value}
                    type="button"
                    onClick={() => setNewType(vt.value)}
                    className="py-3 rounded-xl flex items-center justify-center gap-2 text-sm font-semibold transition-all"
                    style={{
                      background: newType === vt.value ? 'rgba(0,229,160,0.12)' : '#1c1c26',
                      border: `2px solid ${newType === vt.value ? '#00e5a0' : 'rgba(255,255,255,0.07)'}`,
                      color: newType === vt.value ? '#00e5a0' : '#7a798a',
                    }}
                  >
                    <span>{vt.icon}</span> {vt.label}
                  </button>
                ))}
              </div>

              {(newType === 'pet' || newType === 'child') && (
                <label className="flex flex-col items-center justify-center gap-2 py-4 rounded-2xl cursor-pointer transition-all hover:opacity-90"
                  style={{ background: '#1c1c26', border: `2px dashed ${newPhotoPreview ? '#00e5a0' : 'rgba(255,255,255,0.15)'}` }}>
                  {newPhotoPreview ? (
                    <img src={newPhotoPreview} alt="preview" className="w-20 h-20 rounded-full object-cover" style={{ border: '2px solid #00e5a0' }} />
                  ) : (
                    <>
                      <span className="text-3xl">{newType === 'pet' ? '🐾' : '👶'}</span>
                      <span className="text-xs text-brand-muted">Tap to upload a photo (optional)</span>
                    </>
                  )}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (!file) return
                    setNewPhotoFile(file)
                    setNewPhotoPreview(URL.createObjectURL(file))
                  }} />
                </label>
              )}
              {newPhotoPreview && (
                <button type="button" onClick={() => { setNewPhotoFile(null); setNewPhotoPreview(null) }}
                  className="text-xs text-brand-muted hover:text-red-400 transition-colors w-full text-center -mt-2">
                  Remove photo
                </button>
              )}

              {newType === 'pet' && (
                <>
                  <input
                    type="text"
                    value={newPetName}
                    onChange={(e) => setNewPetName(e.target.value)}
                    placeholder="Pet's name (e.g. Bruno)"
                    className="w-full px-4 py-3 rounded-2xl text-brand-text placeholder-brand-muted outline-none font-bold text-center"
                    style={{ background: '#1c1c26', border: '1px solid rgba(255,255,255,0.1)' }}
                    required
                  />
                  <div className="grid grid-cols-3 gap-2">
                    {[{ value: 'dog', label: 'Dog', icon: '🐕' }, { value: 'cat', label: 'Cat', icon: '🐈' }, { value: 'bird', label: 'Bird', icon: '🐦' }, { value: 'rabbit', label: 'Rabbit', icon: '🐇' }, { value: 'other', label: 'Other', icon: '🐾' }].map((pa) => (
                      <button
                        key={pa.value}
                        type="button"
                        onClick={() => setNewPetAnimal(pa.value)}
                        className="py-2.5 rounded-xl flex items-center justify-center gap-1.5 text-xs font-semibold transition-all"
                        style={{
                          background: newPetAnimal === pa.value ? 'rgba(0,229,160,0.12)' : '#1c1c26',
                          border: `2px solid ${newPetAnimal === pa.value ? '#00e5a0' : 'rgba(255,255,255,0.07)'}`,
                          color: newPetAnimal === pa.value ? '#00e5a0' : '#7a798a',
                        }}
                      >
                        <span>{pa.icon}</span> {pa.label}
                      </button>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={newPetBreed}
                    onChange={(e) => setNewPetBreed(e.target.value)}
                    placeholder="Breed (optional, e.g. Labrador)"
                    className="w-full px-4 py-3 rounded-2xl text-brand-text placeholder-brand-muted outline-none"
                    style={{ background: '#1c1c26', border: '1px solid rgba(255,255,255,0.1)' }}
                  />
                </>
              )}

              {newType === 'child' && (
                <>
                  <input
                    type="text"
                    value={newPetName}
                    onChange={(e) => setNewPetName(e.target.value)}
                    placeholder="Child's name (e.g. Arjun)"
                    className="w-full px-4 py-3 rounded-2xl text-brand-text placeholder-brand-muted outline-none font-bold text-center"
                    style={{ background: '#1c1c26', border: '1px solid rgba(255,255,255,0.1)' }}
                    required
                  />
                  <input
                    type="text"
                    value={newPetBreed}
                    onChange={(e) => setNewPetBreed(e.target.value)}
                    placeholder="e.g. 5 years old male, wearing blue shirt"
                    className="w-full px-4 py-3 rounded-2xl text-brand-text placeholder-brand-muted outline-none"
                    style={{ background: '#1c1c26', border: '1px solid rgba(255,255,255,0.1)' }}
                  />
                </>
              )}

              {newType !== 'pet' && newType !== 'child' && (
                <input
                  type="text"
                  value={newPlate}
                  onChange={(e) => setNewPlate(e.target.value.toUpperCase())}
                  placeholder="Number Plate (e.g. MH 02 AB 1234)"
                  className="w-full px-4 py-3 rounded-2xl text-brand-text placeholder-brand-muted outline-none font-bold tracking-widest text-center"
                  style={{ background: '#1c1c26', border: '1px solid rgba(255,255,255,0.1)' }}
                  required
                />
              )}

              <input
                type="tel"
                value={newEmergency}
                onChange={(e) => setNewEmergency(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="Emergency contact (optional)"
                className="w-full px-4 py-3 rounded-2xl text-brand-text placeholder-brand-muted outline-none"
                style={{ background: '#1c1c26', border: '1px solid rgba(255,255,255,0.1)' }}
              />

              <button
                type="submit"
                disabled={addLoading}
                className="w-full py-3.5 rounded-2xl font-syne font-bold text-bg flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ background: '#00e5a0' }}
              >
                {addLoading && <Loader2 size={16} className="animate-spin" />}
                {newType === 'pet' ? 'Add Pet Tag' : newType === 'child' ? 'Add Child Tag' : 'Add Vehicle'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Edit Tag Modal */}
      {editVehicle && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
          onClick={(e) => e.target === e.currentTarget && setEditVehicle(null)}
        >
          <div
            className="w-full max-w-[480px] rounded-t-3xl sm:rounded-3xl p-5 animate-slide-up"
            style={{ background: '#13131a', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-syne font-bold text-lg">
                {editVehicle.type === 'pet' ? 'Edit Pet Tag' : editVehicle.type === 'child' ? 'Edit Child Tag' : 'Edit Vehicle'}
              </h3>
              <button onClick={() => setEditVehicle(null)} className="text-brand-muted hover:text-brand-text">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleEditSave} className="space-y-4">
              {(editVehicle.type === 'pet' || editVehicle.type === 'child') ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-1.5">
                      {editVehicle.type === 'pet' ? 'Pet Name' : "Child's Name"}
                    </label>
                    <input
                      type="text"
                      value={editPetName}
                      onChange={(e) => setEditPetName(e.target.value)}
                      className="w-full px-4 py-3 rounded-2xl text-brand-text placeholder-brand-muted outline-none font-bold text-center"
                      style={{ background: '#1c1c26', border: '1px solid rgba(255,255,255,0.1)' }}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-1.5">
                      {editVehicle.type === 'pet' ? 'Breed' : 'Age / Description'}
                      <span className="text-brand-muted font-normal ml-1">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={editPetBreed}
                      onChange={(e) => setEditPetBreed(e.target.value)}
                      placeholder={editVehicle.type === 'pet' ? 'e.g. Labrador' : 'e.g. 5 years old male, wearing blue shirt'}
                      className="w-full px-4 py-3 rounded-2xl text-brand-text placeholder-brand-muted outline-none"
                      style={{ background: '#1c1c26', border: '1px solid rgba(255,255,255,0.1)' }}
                    />
                  </div>
                </>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-brand-text mb-1.5">Number Plate</label>
                  <input
                    type="text"
                    value={editPlate}
                    onChange={(e) => setEditPlate(e.target.value.toUpperCase())}
                    className="w-full px-4 py-3 rounded-2xl text-brand-text placeholder-brand-muted outline-none font-bold tracking-widest text-center"
                    style={{ background: '#1c1c26', border: '1px solid rgba(255,255,255,0.1)', fontSize: '1.1rem' }}
                    required
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={editLoading}
                className="w-full py-3.5 rounded-2xl font-syne font-bold text-bg flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ background: '#00e5a0' }}
              >
                {editLoading && <Loader2 size={16} className="animate-spin" />}
                Save Changes
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
